import { useState, useRef, useEffect, useCallback } from 'react';
import { useWakeLock } from '../hooks/useWakeLock';
import { useCapabilities } from '../hooks/useCapabilities';
import { getTimerHistory, saveTimerHistory } from '../api/timer';
import { TimerAudioManager } from '../components/timer/TimerAudioManager';
import { stepValue, TIMER_PRESETS, findActivePreset } from './timerStepper.logic';
import './TimerStopwatch.css';

// Format remaining ms as MM:SS
function formatTime(ms) {
  if (ms == null || ms < 0) ms = 0;
  const totalSeconds = Math.ceil(ms / 1000);
  const ss = totalSeconds % 60;
  const mm = Math.floor(totalSeconds / 60);
  return `${String(mm).padStart(2, '0')}:${String(ss).padStart(2, '0')}`;
}

// Format a history entry as a human-readable duration string
function formatDuration({ hours, minutes, seconds }) {
  const parts = [];
  if (hours > 0) parts.push(`${hours}h`);
  if (minutes > 0) parts.push(`${minutes}m`);
  if (seconds > 0 || parts.length === 0) parts.push(`${seconds}s`);
  return parts.join(' ');
}

// Format ISO date as relative label
function formatRelativeDate(isoString) {
  try {
    const date = new Date(isoString);
    const now = new Date();
    const diffMs = now - date;
    const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
    if (diffDays === 0) return 'Heute';
    if (diffDays === 1) return 'Gestern';
    if (diffDays < 7) return `vor ${diffDays} Tagen`;
    return date.toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit' });
  } catch {
    return '';
  }
}

const RADIUS = 90;
const CIRCUMFERENCE = 2 * Math.PI * RADIUS;

export default function TimerPage() {
  const [inputHours, setInputHours] = useState(0);
  const [inputMinutes, setInputMinutes] = useState(5);
  const [inputSeconds, setInputSeconds] = useState(0);
  const [remaining, setRemaining] = useState(null);   // null = not started
  const [running, setRunning] = useState(false);
  const [finished, setFinished] = useState(false);
  const [soundsEnabled, setSoundsEnabled] = useState(true);
  const [history, setHistory] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(true);

  const endTimeRef = useRef(null);
  const intervalRef = useRef(null);
  const lastBeepSecondRef = useRef(null);
  const audioRef = useRef(null);
  const soundsEnabledRef = useRef(true);
  const longPressTimeoutRef = useRef(null);
  const longPressIntervalRef = useRef(null);
  if (audioRef.current === null) {
    audioRef.current = new TimerAudioManager();
  }

  // Cleanup AudioManager (removes visibilitychange/touchstart listeners)
  useEffect(() => () => audioRef.current?.destroy(), []);

  useEffect(() => () => {
    clearTimeout(longPressTimeoutRef.current);
    clearInterval(longPressIntervalRef.current);
  }, []);
  const inputsRef = useRef({ hours: 0, minutes: 5, seconds: 0 });
  const remainingRef = useRef(null);

  // Keep remainingRef in sync with state
  useEffect(() => { remainingRef.current = remaining; }, [remaining]);
  useEffect(() => { soundsEnabledRef.current = soundsEnabled; }, [soundsEnabled]);

  useWakeLock(running);

  const { isGuest } = useCapabilities();

  // Load history on mount — Timer-Verlauf nur für eingeloggte Nutzer (Gäste: kein Backend-Konto)
  useEffect(() => {
    if (isGuest) {
      setHistoryLoading(false);
      return;
    }
    getTimerHistory()
      .then(data => setHistory(Array.isArray(data) ? data : []))
      .catch(() => setHistory([]))
      .finally(() => setHistoryLoading(false));
  }, [isGuest]);

  // Page Visibility re-sync
  useEffect(() => {
    const handleVisibility = () => {
      if (!document.hidden && running && endTimeRef.current) {
        setRemaining(Math.max(0, endTimeRef.current - Date.now()));
      }
    };
    document.addEventListener('visibilitychange', handleVisibility);
    return () => document.removeEventListener('visibilitychange', handleVisibility);
  }, [running]);

  // Countdown interval
  useEffect(() => {
    if (!running) {
      clearInterval(intervalRef.current);
      return;
    }
    intervalRef.current = setInterval(() => {
      const r = endTimeRef.current - Date.now();
      if (r <= 0) {
        clearInterval(intervalRef.current);
        setRemaining(0);
        setRunning(false);
        setFinished(true);
        if (soundsEnabledRef.current) audioRef.current.playEndSignal();
      } else {
        setRemaining(r);
        const s = Math.ceil(r / 1000);
        if (r <= 10000 && s !== lastBeepSecondRef.current) {
          lastBeepSecondRef.current = s;
          if (soundsEnabledRef.current) audioRef.current.playCountdownBeep();
        }
      }
    }, 200);
    return () => clearInterval(intervalRef.current);
  }, [running]);

  const saveToHistory = useCallback((h, m, s) => {
    const entry = { hours: h, minutes: m, seconds: s, last_used: new Date().toISOString() };
    setHistory(prev => {
      const filtered = prev.filter(
        e => !(e.hours === h && e.minutes === m && e.seconds === s)
      );
      const updated = [entry, ...filtered].slice(0, 10);
      // Schedule API call outside the updater to avoid Strict Mode double-invocation.
      // Gäste haben kein Backend-Konto → kein Persistieren (apiFetch würde sonst auf /login umleiten).
      if (!isGuest) {
        setTimeout(() => saveTimerHistory(updated).catch(() => {}), 0);
      }
      return updated;
    });
  }, [isGuest]);

  const handleStart = useCallback(async () => {
    const currentRemaining = remainingRef.current;
    if (currentRemaining !== null) {
      // Resume from paused state
      await audioRef.current.unlock();
      endTimeRef.current = Date.now() + currentRemaining;
      lastBeepSecondRef.current = null;
      setRunning(true);
      setFinished(false);
    } else {
      // Fresh start from inputs
      const h = inputsRef.current.hours;
      const m = inputsRef.current.minutes;
      const s = inputsRef.current.seconds;
      const totalMs = (h * 3600 + m * 60 + s) * 1000;
      if (totalMs <= 0) return;
      await audioRef.current.unlock();
      endTimeRef.current = Date.now() + totalMs;
      lastBeepSecondRef.current = null;
      setRemaining(totalMs);
      setRunning(true);
      setFinished(false);
      saveToHistory(h, m, s);
    }
  }, [saveToHistory]);

  const handleStop = useCallback(() => {
    clearInterval(intervalRef.current);
    setRunning(false);
  }, []);

  const handleReset = useCallback(() => {
    clearInterval(intervalRef.current);
    setRunning(false);
    setFinished(false);
    setRemaining(null);
    endTimeRef.current = null;
    lastBeepSecondRef.current = null;
  }, []);

  const applyHistoryEntry = useCallback((entry) => {
    handleReset();
    setInputHours(entry.hours);
    setInputMinutes(entry.minutes);
    setInputSeconds(entry.seconds);
    inputsRef.current = { hours: entry.hours, minutes: entry.minutes, seconds: entry.seconds };
  }, [handleReset]);

  const applyStep = useCallback((field, delta) => {
    if (field === 'hours') {
      setInputHours(prev => {
        const val = stepValue(prev, delta, 0, 23, false);
        inputsRef.current.hours = val;
        return val;
      });
    } else if (field === 'minutes') {
      setInputMinutes(prev => {
        const val = stepValue(prev, delta, 0, 59, true);
        inputsRef.current.minutes = val;
        return val;
      });
    } else {
      setInputSeconds(prev => {
        const val = stepValue(prev, delta, 0, 59, true);
        inputsRef.current.seconds = val;
        return val;
      });
    }
  }, []);

  const startStep = useCallback((field, delta) => {
    applyStep(field, delta);
    longPressTimeoutRef.current = setTimeout(() => {
      longPressIntervalRef.current = setInterval(() => applyStep(field, delta), 125);
    }, 500);
  }, [applyStep]);

  const stopStep = useCallback(() => {
    clearTimeout(longPressTimeoutRef.current);
    clearInterval(longPressIntervalRef.current);
  }, []);

  // SVG ring
  const totalMs = (inputHours * 3600 + inputMinutes * 60 + inputSeconds) * 1000;
  const progress = remaining !== null && totalMs > 0 ? remaining / totalMs : 1;
  const offset = CIRCUMFERENCE * (1 - progress);

  const isInputPhase = !running && !finished && remaining === null;
  const isRunning = running;
  const isPaused = !running && remaining !== null && !finished;

  return (
    <div className="page-content">
      <h4 className="mb-4" style={{ color: 'var(--color-text)', fontWeight: 700 }}>Timer</h4>

      {/* Im Lauf (Countdown/Pause/Fertig) bleibt die Karte dunkel, unabhängig
          vom App-Modus; nur die Eingabephase folgt Hell/Dunkel. */}
      <div
        className="tools-card"
        data-theme={isInputPhase ? undefined : 'dark'}
        data-bs-theme={isInputPhase ? undefined : 'dark'}
      >
        {/* Input phase: show stepper */}
        {isInputPhase && (
          <div className="timer-stepper-row mb-4">
            {[
              { field: 'hours',   label: 'Stunden',  value: inputHours   },
              { field: 'minutes', label: 'Minuten',  value: inputMinutes },
              { field: 'seconds', label: 'Sekunden', value: inputSeconds },
            ].map(({ field, label, value }, i) => (
              <div key={field} style={{ display: 'contents' }}>
                {i > 0 && <span className="timer-stepper-sep">:</span>}
                <div className="timer-stepper-unit">
                  <span className="timer-stepper-lbl">{label}</span>
                  <div className="timer-stepper-box">
                    <button
                      className="timer-stepper-btn"
                      aria-label={`${label} erhöhen`}
                      onPointerDown={() => startStep(field, 1)}
                      onPointerUp={stopStep}
                      onPointerLeave={stopStep}
                      onPointerCancel={stopStep}
                    >▲</button>
                    <div className="timer-stepper-num" data-testid="stepper-num">
                      {String(value).padStart(2, '0')}
                    </div>
                    <button
                      className="timer-stepper-btn"
                      aria-label={`${label} verringern`}
                      onPointerDown={() => startStep(field, -1)}
                      onPointerUp={stopStep}
                      onPointerLeave={stopStep}
                      onPointerCancel={stopStep}
                    >▼</button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Countdown phase: show SVG ring */}
        {!isInputPhase && (
          <div className="timer-ring-wrapper">
            <svg width="220" height="220" viewBox="0 0 220 220">
              {/* Track */}
              <circle
                cx="110" cy="110" r={RADIUS}
                fill="none" stroke="#1e293b" strokeWidth="10"
              />
              {/* Progress arc */}
              <circle
                cx="110" cy="110" r={RADIUS}
                fill="none"
                stroke="var(--color-accent)"
                strokeWidth="10"
                strokeDasharray={CIRCUMFERENCE}
                strokeDashoffset={offset}
                strokeLinecap="round"
                transform="rotate(-90 110 110)"
                style={{ transition: 'stroke-dashoffset 0.2s linear' }}
                className={finished ? 'ring-finished' : ''}
              />
              {/* Center time */}
              <text
                x="110" y="118"
                textAnchor="middle"
                fontSize="32"
                fontFamily="Outfit, monospace"
                fill="#f1f5f9"
                fontWeight="700"
              >
                {formatTime(remaining)}
              </text>
            </svg>
          </div>
        )}

        {/* Finished label */}
        {finished && (
          <div className="timer-finished-label">Fertig!</div>
        )}

        {/* Controls */}
        <div className="tools-btn-row">
          {isInputPhase && (
            <button className="tools-btn-primary" onClick={handleStart}>
              Start
            </button>
          )}
          {isRunning && (
            <button className="tools-btn-primary" onClick={handleStop}>
              Pause
            </button>
          )}
          {isPaused && (
            <>
              <button className="tools-btn-primary" onClick={handleStart}>
                Weiter
              </button>
              <button className="tools-btn-ghost" onClick={handleReset}>
                Reset
              </button>
            </>
          )}
          {finished && (
            <button className="tools-btn-ghost" onClick={handleReset}>
              Neu
            </button>
          )}
        </div>

        {isInputPhase && (
          <>
            <div className="form-check d-flex align-items-center justify-content-center mt-3">
              <input
                type="checkbox"
                className="form-check-input"
                id="timer_sounds_enabled"
                checked={soundsEnabled}
                onChange={e => setSoundsEnabled(e.target.checked)}
              />
              <label className="form-check-label hiit-sounds-label ms-2" htmlFor="timer_sounds_enabled">
                <i className="bi bi-volume-up me-1" />
                Töne aktivieren (Beep &amp; Übergänge)
              </label>
            </div>
            <hr className="timer-preset-divider" />
            <div className="timer-history-title">Quickstart</div>
            <div className="timer-preset-grid">
              {TIMER_PRESETS.map((preset, i) => {
                const isActive = findActivePreset(inputHours, inputMinutes, inputSeconds) === i;
                return (
                  <button
                    key={preset.label}
                    data-testid="preset-tile"
                    className={`timer-preset-tile${isActive ? ' active' : ''}`}
                    onClick={() => {
                      setInputHours(preset.hours);
                      setInputMinutes(preset.minutes);
                      setInputSeconds(preset.seconds);
                      inputsRef.current = { hours: preset.hours, minutes: preset.minutes, seconds: preset.seconds };
                    }}
                  >
                    {preset.label}
                  </button>
                );
              })}
            </div>
          </>
        )}
      </div>

      {/* History tiles */}
      {!historyLoading && history.length > 0 && (
        <div className="tools-card">
          <div className="timer-history-title">Zuletzt verwendet</div>
          <div className="timer-history-grid">
            {history.map((entry, i) => (
              <button
                key={`${entry.hours}-${entry.minutes}-${entry.seconds}`}
                className="timer-history-tile"
                onClick={() => applyHistoryEntry(entry)}
              >
                <span className="timer-history-time">{formatDuration(entry)}</span>
                <span className="timer-history-date">{formatRelativeDate(entry.last_used)}</span>
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
